// ---------------------------------------------------------------------------
// As-Is snapshot — the source-truth original captured before it is improved
// into the to-be. Persisted on the project's `settings.asIsSnapshot` (operator
// decision 2026-08-12: the lightest of the three persistence models — no new
// backend route or schema, because `loadProject` already returns `settings`).
//
// The As-Is mode (G1 alternative A) renders these blocks through the SAME
// client-side preview pipeline the to-be uses; the blocks are source-derived
// (options.sourceDerived / element.source), so `buildPreviewRenderPackBytes`
// renders the original JSON verbatim for visual parity.
//
// This module owns ONLY the read-side trust boundary + type, mirroring
// `readIntakeDecisionsFromSettings` (chat/services/generation-provenance.ts):
// `settings` is untyped at the store boundary, so the snapshot is
// re-validated here and any mismatch returns null (never a partial snapshot
// that would render a broken "as-is").
// ---------------------------------------------------------------------------

import { isAsIsFrameEnabled } from './as-is-frame-flag'

export const AS_IS_SNAPSHOT_SCHEMA_VERSION = 'studio-as-is-snapshot.v1' as const

export interface AsIsSnapshot {
  schemaVersion: typeof AS_IS_SNAPSHOT_SCHEMA_VERSION
  /** How the original was captured, e.g. 'legacy-screen' | 'figma' | 'openpencil'. */
  sourceKind: string
  /** ISO timestamp the snapshot was taken. */
  capturedAt: string
  /** Renderable source-derived blocks, keyed by block name. */
  blocks: Record<string, unknown>
  /** 붙여넣은 원본 마크업(정제본). 있으면 이것이 소스 진실이고, 없으면 blocks가 파생 골격이다. */
  sourceMarkup?: { markup: string; removed: number }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * FINAL-REVIEW M2 — `sourceMarkup.removed` is a COUNT of elements, and the
 * pane substitutes it straight into "{count} element(s) removed for safety".
 * The old check (`typeof === 'number'` + `Number.isFinite` + `>= 0`) let
 * `1.5` through, so the user was told "1.5 element(s) removed for safety" —
 * every other malformed shape at this boundary already rejected the whole
 * snapshot. A count is a non-negative integer; nothing else is one.
 */
function isElementCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

/** A block is previewable when it exposes a render root: `ui.root` or bare `root`
 *  (the same shape `buildPreviewRenderPackBytes` requires). */
function hasRenderableRoot(block: unknown): boolean {
  if (!isRecord(block)) return false
  const ui = block.ui
  if (isRecord(ui) && ui.root !== undefined) return true
  return block.root !== undefined
}

function renderableRoot(block: unknown): Record<string, unknown> | null {
  if (!isRecord(block)) return null
  const ui = block.ui
  if (isRecord(ui) && isRecord(ui.root)) return ui.root
  return isRecord(block.root) ? block.root : null
}

/**
 * The design-compiler bypass in `render-pack-preview.ts`
 * (`isSourceProjectedElement`) is keyed on THIS marker set — not on
 * `sourceKind`. A root without it is compiled and tokenized, so the "original"
 * would be rendered restyled, which the module header forbids.
 *
 * Deliberately checked on the ROOT only (the bypass also accepts a marked
 * descendant): a snapshot must declare source-projection for the whole block,
 * not inherit it from one leaf. Deliberately NOT imported from
 * `render-pack-preview.ts` either — that module pulls in the design compiler
 * and the runtime encoder, and this one is loaded eagerly by the tab gate.
 * If the marker set there changes, change it here too.
 */
function isSourceProjectedRoot(root: Record<string, unknown>): boolean {
  const options = root.options
  return (
    (isRecord(options) && options.sourceDerived === true) ||
    root.source !== undefined ||
    root.sourceDesign !== undefined
  )
}

/**
 * Read + re-validate `settings.asIsSnapshot`. Returns a clean snapshot or null;
 * null when absent, malformed, tagged with a different schema version, or
 * carrying any block that is not previewable OR not source-projected — never a
 * partial, and never a block the preview pipeline would tokenize.
 */
export function readAsIsSnapshotFromSettings(settings: unknown): AsIsSnapshot | null {
  if (!isRecord(settings)) return null
  const raw = settings.asIsSnapshot
  if (!isRecord(raw)) return null
  if (raw.schemaVersion !== AS_IS_SNAPSHOT_SCHEMA_VERSION) return null
  if (!isNonEmptyString(raw.sourceKind)) return null
  if (!isNonEmptyString(raw.capturedAt)) return null
  if (!isRecord(raw.blocks)) return null

  const entries = Object.entries(raw.blocks)
  if (entries.length === 0) return null
  for (const [, block] of entries) {
    if (!hasRenderableRoot(block)) return null
    const root = renderableRoot(block)
    if (!root || !isSourceProjectedRoot(root)) return null
  }

  let sourceMarkup: AsIsSnapshot['sourceMarkup']
  if (raw.sourceMarkup !== undefined) {
    const sm = raw.sourceMarkup
    // FINAL-REVIEW M2 — `removed` is a COUNT of elements, and the pane
    // substitutes it straight into "{count} element(s) removed for safety".
    // `Number.isFinite` let `1.5` through the boundary and the user was told
    // "1.5 element(s) removed for safety"; every other malformed shape here
    // already rejected the whole snapshot. `Number.isInteger` is also the
    // type guard (false for non-numbers, NaN and Infinity), so it replaces
    // the typeof + isFinite pair rather than joining it.
    if (
      !isRecord(sm) ||
      !isNonEmptyString(sm.markup) ||
      !isElementCount(sm.removed)
    ) {
      return null
    }
    sourceMarkup = { markup: sm.markup, removed: sm.removed }
  }

  return {
    schemaVersion: AS_IS_SNAPSHOT_SCHEMA_VERSION,
    sourceKind: raw.sourceKind,
    capturedAt: raw.capturedAt,
    blocks: raw.blocks,
    ...(sourceMarkup ? { sourceMarkup } : {}),
  }
}

/**
 * Single source of truth for the source-truth-vs-derived distinction every
 * As-Is provenance label must follow: the pane heading (as-is-view.tsx), the
 * artifact-mode tab (artifact-mode-switcher.tsx), the chat rail description
 * (studio-task-center.tsx), the loading placeholder (IDEShellLayout.tsx),
 * and the chat composer placeholder (studio-composer.tsx /
 * studio-task-orchestrator.ts). Task-4 fix-round-1 fixed the pane alone;
 * fix-round-2 exists because the tab and the rail decided this independently
 * and drifted from it — a live-browser E2E caught the pane saying "Original"
 * while the tab and rail still said the derived-structure wording for the
 * SAME snapshot. Every caller must go through THIS function rather than
 * re-deriving the answer, so they cannot drift apart again.
 *
 * PR #2072 — gate-aware (operator finding 2026-08-13): a snapshot can carry
 * `sourceMarkup` while the As-Is markup FRAME is gated off
 * (`isAsIsFrameEnabled()`, as-is-frame-flag.ts — two open Critical sanitizer
 * defects). In that state no surface actually renders the original bytes;
 * the pane falls back to the derived skeleton. A predicate that still
 * answered `true` here would make every one of the five surfaces above say
 * "Original" for a snapshot none of them are showing — reintroducing, via
 * this one shared decision point, exactly the kind of self-contradiction
 * this function was centralized to prevent. So: "is this the original" is
 * `false` whenever the original cannot actually be shown, regardless of what
 * is stored. This is a DISPLAY predicate — capture and storage of
 * `sourceMarkup` are unaffected (legacy-screen-as-is-capture.ts writes it
 * regardless of this flag, and `readAsIsSnapshotFromSettings` above still
 * returns it unmodified for callers that need the stored value, e.g. the
 * pane itself, which reads `snapshot.sourceMarkup` through its own gate
 * check rather than through this function).
 */
export function hasAsIsSourceMarkup(settings: unknown): boolean {
  return (
    isAsIsFrameEnabled() &&
    readAsIsSnapshotFromSettings(settings)?.sourceMarkup !== undefined
  )
}

/** The switcher's fixed base tabs (mode-switcher OPTIONS order). */
const BASE_ARTIFACT_MODE_TABS = [
  'primary',
  'design',
  'guide',
  'insights',
] as const

export type ArtifactModeTab =
  | (typeof BASE_ARTIFACT_MODE_TABS)[number]
  | 'as-is'

/**
 * The artifact-mode tab list for the current project. The As-Is tab appears
 * ONLY when a valid `settings.asIsSnapshot` exists (a conversion project), so a
 * greenfield project never shows an empty As-Is tab (G1 alternative A's
 * conditional-display requirement). It sits directly after `primary` so the
 * original and the to-be are adjacent for comparison.
 */
export function resolveArtifactModeTabs(settings: unknown): ArtifactModeTab[] {
  if (!readAsIsSnapshotFromSettings(settings)) {
    return [...BASE_ARTIFACT_MODE_TABS]
  }
  return ['primary', 'as-is', 'design', 'guide', 'insights']
}

